import { landingAfterSignIn } from '../auth/lastProperty';
import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Unauthenticated, api } from '../api/client';
import {
  assuranceLevel,
  authConfigured,
  AuthError,
  chooseOrganization,
  completePasswordReset,
  current,
  exchangeLink,
  hasStoredSession,
  hasVerifiedFactor,
  listFactors,
  requestPasswordReset,
  setPassword,
  signIn,
  signOut,
  verifyTotp,
} from '../auth/session';
import { EntryLayout } from './EntryLayout';

/**
 * B1-01 Global entry, and B5-08 recovery, invitation and MFA.
 *
 * **A sign-in failure never says which half was wrong.** "That email and
 * password did not match an account" covers a wrong password, a wrong address
 * and an unconfirmed one alike. Distinguishing them is a courtesy to the
 * person signing in and an oracle for everybody else — it turns the form into
 * a way to ask whether an address is a customer.
 *
 * **A password reset never says whether the address exists**, for the same
 * reason, and the confirmation says so out loud rather than implying delivery.
 *
 * **A recovery token is read from the URL fragment, never a query string.** A
 * fragment is not sent to the server, so it does not reach an access log, a
 * referrer header or an analytics beacon. Supabase puts it there; this reads
 * it from there and nowhere else.
 *
 * **MFA is the provider's, and the shell says so.** Owner and Admin must
 * present a second factor (E01-S03, E00-S12), and assurance is read from the
 * provider's assertion on every request rather than from a stored flag — a
 * flag says the user enrolled, and the question is whether they satisfied it
 * on this session. So there is nothing for this screen to verify; when the API
 * refuses for assurance, this explains what happened.
 */

/**
 * A password field that can be read.
 *
 * Hiding what somebody types is a defence against a person behind them, and on
 * a laptop at a kitchen table there is nobody behind them — while a password
 * they cannot check is one they mistype, and a mistyped password on the reset
 * screen becomes a lockout they discover tomorrow.
 *
 * So the choice belongs to the person, and it defaults to hidden.
 *
 * The toggle is a button with a name, not a bare icon: `aria-pressed` carries
 * the state to a screen reader, and the label changes with it, so somebody who
 * cannot see the eye still knows whether their password is on screen.
 */
function PasswordField({
  id,
  label,
  autoComplete,
  value,
  onChange,
}: {
  id: string;
  label: string;
  autoComplete: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const [shown, setShown] = useState(false);
  return (
    <>
      <label htmlFor={id}>{label}</label>
      <div className="password-field">
        <input
          id={id}
          type={shown ? 'text' : 'password'}
          autoComplete={autoComplete}
          required
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
        <button
          type="button"
          className="password-field__reveal"
          aria-pressed={shown}
          aria-label={shown ? 'Hide password' : 'Show password'}
          onClick={() => setShown((v) => !v)}
        >
          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
            <path
              d="M1.5 12S5 5.5 12 5.5 22.5 12 22.5 12 19 18.5 12 18.5 1.5 12 1.5 12Z"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <circle cx="12" cy="12" r="3.2" fill="none" stroke="currentColor" strokeWidth="1.6" />
            {!shown && (
              <path
                d="M4 20 20 4"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            )}
          </svg>
        </button>
      </div>
    </>
  );
}

function Configured({ children }: { children: React.ReactNode }) {
  if (authConfigured()) return <>{children}</>;
  return (
    <p role="alert">
      Sign-in is not configured in this build. <code>VITE_SUPABASE_URL</code> and{' '}
      <code>VITE_SUPABASE_PUBLISHABLE_KEY</code> are read at build time, so setting them after a
      build has no effect — the Pages project has to be rebuilt.
    </p>
  );
}

/** B1-01 — the way in. */
export function Login() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Where the user was going before the session ended. Sent by the guard, and
  // restored on success — being bounced to the portfolio after signing in
  // again is how somebody loses the thing they were reading.
  //
  // With no such destination, E07-S01 lands on the last property's Attention
  // rather than the portfolio: "Property operation is primary; Portfolio is
  // secondary" (S10B §3). `landingAfterSignIn` leaves an explicit `next`
  // alone, because that is a screen somebody actually asked for.
  const next = params.get('next') || '/';

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signIn(email, password);

      // A password gets you `aal1`. If this account already carries a verified
      // second factor, present it now rather than letting the API refuse and
      // hoping somebody reads a link on an error screen.
      //
      // That is what happened: an Owner signed in, got a 403 naming no reason,
      // and the enrolment they had already completed was one click away behind
      // a sentence. Supabase's record showed the factor verified and not
      // challenged once that day.
      //
      // Only when a factor exists. An account without one may not need one —
      // Manager and Inspector do not — and sending them to an enrolment screen
      // they were never asked for is the opposite mistake.
      if (assuranceLevel() === 'aal1' && (await hasVerifiedFactor())) {
        navigate(`/mfa?next=${encodeURIComponent(landingAfterSignIn(next))}`, {
          replace: true,
        });
        return;
      }

      navigate(landingAfterSignIn(next), { replace: true });
    } catch (e) {
      setError(e instanceof AuthError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <EntryLayout>
      <h1>Sign in to Premise Sense</h1>
      <Configured>
        <form onSubmit={submit}>
          <label htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          <PasswordField
            id="password"
            label="Password"
            autoComplete="current-password"
            value={password}
            onChange={setPassword}
          />

          {/* One message for every failure, on purpose. */}
          {error && <p role="alert">{error}</p>}

          <button type="submit" disabled={busy}>
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <p className="basis">
          <Link to="/reset">Forgotten your password?</Link>
        </p>
        <p className="basis basis--unknown">
          Accounts are created by invitation. If you have been invited, use the link in that email
          rather than this form.
        </p>
      </Configured>
    </EntryLayout>
  );
}

/** B5-08 — request a reset. */
export function RequestReset() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    try {
      await requestPasswordReset(email);
    } catch (e) {
      // The provider answers the same for an address with no account, so a
      // refusal here is never about the address — and saying so reveals
      // nothing. It used to be swallowed: Supabase's mail failed with a 500
      // (Development manual test, 2026-09-15) and the screen still said a link
      // was on its way.
      if (e instanceof AuthError && e.code === 'not_configured') {
        setError(e.message);
        return;
      }
      if (e instanceof AuthError && e.status === 429) {
        setError('Too many reset requests. Wait a few minutes, then try again.');
        return;
      }
      // Any other refusal from the provider (a 4xx) is answered exactly as a
      // success, so the page cannot be used to learn which addresses exist.
      // Only a failure to send at all — the provider's 5xx, or no answer —
      // is shown.
      const failedToSend = !(e instanceof AuthError) || e.status === 0 || e.status >= 500;
      if (failedToSend) {
        setError(
          'The reset email could not be sent right now. Nothing is wrong with your address — try again in a few minutes.',
        );
        return;
      }
    }
    setSent(true);
  }

  return (
    <EntryLayout>
      <h1>Reset your password</h1>
      <Configured>
        {sent ? (
          // Says what was done, not what will arrive. "Check your inbox" is a
          // claim about delivery, and telling somebody an email is coming when
          // their address is not on the account is how they wait for an hour.
          <p aria-live="polite">
            If that address has an account, a reset link is on its way. We do not say whether it
            does — an account is a fact about a person, and this form would otherwise answer that
            question for anybody who asked.
          </p>
        ) : (
          <form onSubmit={submit}>
            <label htmlFor="reset-email">Email</label>
            <input
              id="reset-email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            {error && <p role="alert">{error}</p>}
            <button type="submit">Send a reset link</button>
          </form>
        )}
        <p className="basis">
          <Link to="/login">Back to sign in</Link>
        </p>
      </Configured>
    </EntryLayout>
  );
}

let resetLink: URLSearchParams | null = null;

/** Read the reset link's fragment once, then take it out of the address bar.
 *  A fragment is not sent to the server, so it reaches no access log or
 *  referrer header; clearing it keeps it out of the browser history for the
 *  next person at the desk. */
function resetFragment(): URLSearchParams {
  if (window.location.hash || resetLink === null) {
    resetLink = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    if (window.location.hash) window.history.replaceState(null, '', window.location.pathname);
  }
  return resetLink;
}

/** B5-08 — arrive from the link and set a password. */
export function CompleteReset() {
  const navigate = useNavigate();
  const [link] = useState(() => resetFragment());
  // The token-hash flow: a hash of a one-time token, spent when the new
  // password is submitted. A link sent before the email template changed
  // carries the session itself, and still works.
  const tokenHash = link.get('token_hash');
  const legacyToken = link.get('access_token');
  const token = tokenHash ?? legacyToken;
  const [exchanged, setExchanged] = useState(false);
  const [password, setPasswordValue] = useState('');
  const [confirm, setConfirm] = useState('');
  // The second factor, asked for only when the account has one. A reset link
  // proves the mailbox and nothing more, and Supabase will not change the
  // password of an account with two-factor authentication on the strength of
  // the mailbox alone — which is right: otherwise the mailbox would be the
  // whole of an Owner's security.
  const [factorId, setFactorId] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError('The two passwords do not match.');
      return;
    }
    if (password.length < 12) {
      // Length, not composition. A twelve-character passphrase beats an
      // eight-character one with a symbol in it, and a rule demanding symbols
      // produces Password1! on every account in the organization.
      setError('Use at least 12 characters. Length matters more than symbols.');
      return;
    }
    try {
      if (tokenHash) {
        // Spent once. A refused password leaves the session in place, so a
        // second attempt sets the password without spending the link again.
        if (!exchanged) {
          await exchangeLink(tokenHash, 'recovery');
          setExchanged(true);
          resetLink = null;
          if (assuranceLevel() !== 'aal2') {
            const factor = (await listFactors()).find((f) => f.status === 'verified');
            if (factor) {
              // Stop here and ask. The passwords stay filled in, so the next
              // press of the button carries on from this point.
              setFactorId(factor.id);
              return;
            }
          }
        }
        if (factorId && assuranceLevel() !== 'aal2') {
          if (!/^[0-9]{6}$/.test(code)) {
            setError('Enter the six-digit code from your authenticator app.');
            return;
          }
          await verifyTotp(factorId, code);
        }
        await setPassword(password);
        signOut();
      } else {
        await completePasswordReset(legacyToken!, password);
      }
      navigate('/login', { replace: true });
    } catch (e) {
      setError(e instanceof AuthError ? e.message : String(e));
    }
  }

  return (
    <EntryLayout>
      <h1>Choose a new password</h1>
      <Configured>
        {token === null ? (
          <p role="alert">
            This page needs the link from your reset email. Open that link directly rather than
            typing the address.
          </p>
        ) : (
          <form onSubmit={submit}>
            <PasswordField
              id="new-password"
              label="New password"
              autoComplete="new-password"
              value={password}
              onChange={setPasswordValue}
            />
            <PasswordField
              id="confirm-password"
              label="Confirm"
              autoComplete="new-password"
              value={confirm}
              onChange={setConfirm}
            />
            {factorId && (
              <>
                <p className="basis" role="status">
                  This account uses two-factor authentication. Enter the six-digit code from your
                  authenticator app to set the new password.
                </p>
                <label htmlFor="reset-totp">Six-digit code</label>
                <input
                  id="reset-totp"
                  // `text` with a numeric mode: a number field strips leading
                  // zeros, and a code beginning 0 is one in ten.
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ''))}
                />
              </>
            )}
            {error && <p role="alert">{error}</p>}
            <button type="submit">{factorId ? 'Verify and set password' : 'Set password'}</button>
          </form>
        )}
      </Configured>
    </EntryLayout>
  );
}

let inviteLink: URLSearchParams | null = null;

/** Read the invitation link's fragment once and take it out of the address
 *  bar, keeping the query string — it carries the organization, not a
 *  credential. Module level, so a re-render still has what the first took. */
function inviteFragment(): URLSearchParams {
  if (window.location.hash || inviteLink === null) {
    inviteLink = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    if (window.location.hash)
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
  }
  return inviteLink;
}

/**
 * B5-08 / E01-S05 — accept an invitation.
 *
 * The emailed link carries a hash of a one-time provider token — never a
 * session. **Continue** exchanges it, which proves the mailbox and returns a
 * session for the invited account. Two steps rather than one: a mail scanner
 * that opens the link spends nothing, and the person sees which account they
 * are joining as before choosing its password, so a crafted link cannot
 * quietly sign its reader in as its sender.
 *
 * **Accept, then set the password.** Accepting first means a stray or expired
 * token never sets a password on an account that joins nothing; a password
 * that fails after joining is recoverable from the reset page.
 */
export function AcceptInvitation() {
  const { token } = useParams();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const [link] = useState(() => inviteFragment());
  const tokenHash = link.get('th');
  const linkType = link.get('type') === 'magiclink' ? 'magiclink' : 'invite';
  const [exchanged, setExchanged] = useState(false);
  // Without a link in the address: a reload after Continue, whose session is
  // stored.
  const [signedIn, setSignedIn] = useState(
    () => !tokenHash && (current() !== null || hasStoredSession()),
  );
  const otherSession = Boolean(tokenHash) && (current() !== null || hasStoredSession());
  const [organization, setOrganization] = useState(search.get('org') ?? '');
  const [name, setName] = useState('');
  const [password, setPasswordValue] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [joinedWithoutPassword, setJoinedWithoutPassword] = useState(false);
  const orgFromLink = Boolean(search.get('org'));

  async function continueFromLink() {
    setError(null);
    setBusy(true);
    try {
      if (otherSession) signOut();
      await exchangeLink(tokenHash ?? '', linkType);
      inviteLink = null;
      setExchanged(true);
      setSignedIn(true);
    } catch (e) {
      setError(
        e instanceof AuthError
          ? 'That link has expired or has already been used. Ask whoever invited you to send the invitation again — the new link replaces this one.'
          : String(e),
      );
    } finally {
      setBusy(false);
    }
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError('The two passwords do not match.');
      return;
    }
    if (password.length < 12) {
      setError('Use at least 12 characters. Length matters more than symbols.');
      return;
    }
    setBusy(true);
    let joined: { organization_id: string; role: string };
    try {
      joined = await api.acceptInvitation(token ?? '', organization.trim(), name.trim() || null);
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      setError(
        e instanceof Unauthenticated
          ? 'Your sign-in link has expired. Ask whoever invited you to send the invitation again.'
          : /invalid_invitation|HTTP 404|HTTP 422/.test(message)
            ? // One message for expired, used and wrong alike. Which of the
              // three it was is a fact about the invitation, and this page is
              // reachable by anybody with a guess.
              'That invitation is not valid. It may have expired, or already been used. Ask whoever invited you to send another.'
            : message,
      );
      setBusy(false);
      return;
    }
    chooseOrganization(joined.organization_id);
    try {
      await setPassword(password);
    } catch (e) {
      setJoinedWithoutPassword(true);
      setError(e instanceof AuthError ? e.message : String(e));
      setBusy(false);
      return;
    }
    // Owner and Admin cannot use the product without a second factor, so
    // they go to set one up rather than to a screen that would refuse them.
    navigate(joined.role === 'owner' || joined.role === 'admin' ? '/mfa' : '/', {
      replace: true,
    });
  }

  const signedInAs = current()?.email ?? '';

  return (
    <EntryLayout>
      <h1>Join your organization</h1>
      <Configured>
        {joinedWithoutPassword ? (
          <>
            <p role="status">You have joined.</p>
            <p role="alert">{error}</p>
            <p className="basis">
              Set a password from the <Link to="/reset">reset page</Link> using the address you were
              invited at, then sign in.
            </p>
          </>
        ) : tokenHash && !exchanged ? (
          <>
            <p>This link lets you join your organization on Premise Sense.</p>
            {otherSession && (
              <p className="basis">
                This browser is already signed in to Premise Sense. Continuing signs that account
                out.
              </p>
            )}
            {error && <p role="alert">{error}</p>}
            <button type="button" onClick={continueFromLink} disabled={busy}>
              {busy ? 'Checking the link…' : 'Continue'}
            </button>
          </>
        ) : !signedIn ? (
          <p role="alert">
            Open this page from the link in your invitation email. The link is what proves the
            invitation reached you, so typing this address does not work.
          </p>
        ) : (
          <form onSubmit={submit}>
            {signedInAs && (
              <p className="basis">
                Joining as <strong>{signedInAs}</strong>.
              </p>
            )}
            {!orgFromLink && (
              <>
                <label htmlFor="org">Organization</label>
                <input
                  id="org"
                  required
                  value={organization}
                  onChange={(e) => setOrganization(e.target.value)}
                />
              </>
            )}
            <label htmlFor="display-name">Your name</label>
            <input
              id="display-name"
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <p className="basis">How colleagues see you on assignments. Optional.</p>
            <PasswordField
              id="new-password"
              label="New password"
              autoComplete="new-password"
              value={password}
              onChange={setPasswordValue}
            />
            <p className="basis">At least 12 characters. Length matters more than symbols.</p>
            <PasswordField
              id="confirm-password"
              label="Confirm"
              autoComplete="new-password"
              value={confirm}
              onChange={setConfirm}
            />
            {error && <p role="alert">{error}</p>}
            <button type="submit" disabled={busy}>
              {busy ? 'Joining…' : 'Join'}
            </button>
          </form>
        )}
      </Configured>
    </EntryLayout>
  );
}

let emailLink: URLSearchParams | null = null;

/** Read the verification link's fragment once, then take it out of the
 *  address bar. Held at module level so a second render — StrictMode, or a
 *  re-mount — still has what the first one removed. */
function emailLinkFragment(): URLSearchParams {
  if (window.location.hash || emailLink === null) {
    emailLink = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    if (window.location.hash) window.history.replaceState(null, '', window.location.pathname);
  }
  return emailLink;
}

/**
 * §32.3 — confirm a new email address.
 *
 * A button, not an automatic call on page load: mail scanners open links to
 * check them, and a confirmation that fired on load would be consumed by the
 * scanner before the person ever saw it.
 */
export function VerifyEmail() {
  const [link] = useState(() => emailLinkFragment());
  const token = link.get('token');
  const organization = link.get('org');
  const [state, setState] = useState<'ready' | 'busy' | 'done'>('ready');
  const [email, setEmail] = useState<string | null>(null);
  const [signInChanged, setSignInChanged] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function confirmAddress() {
    setState('busy');
    setError(null);
    try {
      const changed = await api.verifyEmailChange<{ email: string; sign_in_changed?: boolean }>(
        token ?? '',
        organization ?? '',
      );
      emailLink = null;
      setEmail(changed.email);
      setSignInChanged(changed.sign_in_changed !== false);
      setState('done');
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      setError(
        /email_unavailable/.test(message)
          ? 'That address is now used by another account, so the change was not made.'
          : /invalid_token|HTTP 404|HTTP 422/.test(message)
            ? 'That link has expired or has already been used. Ask for the change to be made again.'
            : 'The change could not be completed right now. Nothing was changed — open the link again in a few minutes.',
      );
      setState('ready');
    }
  }

  return (
    <EntryLayout>
      <h1>Confirm your new email address</h1>
      {state === 'done' ? (
        <p role="status">
          {signInChanged ? (
            <>
              Confirmed. Sign in with <strong>{email}</strong> from now on.{' '}
              <Link to="/login">Sign in</Link>
            </>
          ) : (
            <>
              Confirmed. Mail now goes to <strong>{email}</strong>. The address used to sign in has
              not changed.
            </>
          )}
        </p>
      ) : !token || !organization ? (
        <p role="alert">
          Open this page from the link in the confirmation email. The link carries what confirms the
          address; typing this page&apos;s address does not.
        </p>
      ) : (
        <>
          <p>Until you confirm, your previous address stays the one you sign in with.</p>
          {error && <p role="alert">{error}</p>}
          <button type="button" onClick={confirmAddress} disabled={state === 'busy'}>
            {state === 'busy' ? 'Confirming…' : 'Confirm this address'}
          </button>
        </>
      )}
    </EntryLayout>
  );
}

/**
 * B5-08 — the MFA challenge, explained rather than performed.
 *
 * Owner and Admin must present a second factor, and assurance is read from the
 * provider's assertion on every request rather than from a stored flag. There
 * is therefore nothing for this screen to verify — the factor is satisfied
 * with the identity provider, and the API simply refuses a session that has
 * not. Rendering an input here would be a form that cannot succeed.
 */
export function MfaRequired() {
  return (
    <EntryLayout>
      <h1>A second factor is required</h1>
      <p>
        Owner and Admin accounts must present a second factor. Your session was established without
        one, so the product is refusing rather than proceeding.
      </p>
      <p className="basis">
        This is checked on every request, not once at sign-in: enrolling in two-factor
        authentication is not the same as having used it on this session, and only the second is
        evidence.
      </p>
      {/* This used to say "complete the second factor with your identity
          provider", which nobody could do: Supabase has no end-user interface
          for TOTP, only an API. An Owner could not use the product and could
          not invite anybody who could. */}
      <p className="basis">
        <Link to="/mfa">Set up or present your second factor</Link>.
      </p>
    </EntryLayout>
  );
}
